(() => {
  "use strict";

  if (window.__lymiaArcade) {
    window.__lymiaArcade.refresh();
    return;
  }
  const app = { refresh() {} };
  window.__lymiaArcade = app;

  const boot = () => {
    if (!document.body?.classList.contains("lymia-japon")) {
      delete window.__lymiaArcade;
      return;
    }

    const DURATION = 900;
    const FAILSAFE = 1200;
    const CART_BURST_LIMIT = 3;
    const CART_CAPSULE_COUNT = 8;
    const BADGE_DEADLINE = Date.parse("2026-10-11T18:01:00+02:00");
    const BADGE_NOTICE = "Dispo le 11/10 à 18h !";
    const BADGE_SELECTOR = ".card__badge .badge, .price__badge-sold-out";
    const originalBadges = new Map();
    let badgeTimer = null;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const decorations = new Set();
    const marked = new Map();
    const cartBursts = new Set();
    const unloadedHeaders = new WeakSet();
    let headerLanterns = null;
    let lanternHeader = null;
    let active = null;
    let refreshTimer = null;
    let suspended = false;
    const isArcade = () => document.documentElement.getAttribute("data-lymia-look") === "arcade";
    const motionAllowed = () => isArcade() && !suspended && !document.hidden && !reduced.matches
      && document.body.getAttribute("data-lj-motion-state") === "running";

    const soldOutTextNode = (node) => {
      if (node.nodeType === 3) return node.data.trim() === "Épuisé" ? node : null;
      for (const child of node.childNodes) {
        const match = soldOutTextNode(child);
        if (match) return match;
      }
      return null;
    };
    const updateSoldOutBadges = () => {
      const pending = Date.now() < BADGE_DEADLINE;
      for (const [badge, saved] of originalBadges) {
        // Shopify owns later rewrites. Only restore the text node we changed
        // while it still holds our temporary label inside the same badge.
        if (!badge.isConnected || !badge.contains(saved.node) || saved.node.data !== BADGE_NOTICE
          || badge.textContent.trim() !== BADGE_NOTICE) originalBadges.delete(badge);
        else if (!pending) { saved.node.data = saved.text; originalBadges.delete(badge); }
      }
      if (pending) document.querySelectorAll(BADGE_SELECTOR).forEach((badge) => {
        if ((!badge.matches(".price__badge-sold-out") && !badge.closest(".product-card-wrapper"))
          || badge.matches(".price__badge-sale") || badge.textContent.trim() !== "Épuisé") return;
        const node = soldOutTextNode(badge);
        if (!node) return;
        originalBadges.set(badge, { node, text: node.data });
        node.data = BADGE_NOTICE;
      });
      if (badgeTimer !== null) window.clearTimeout(badgeTimer);
      badgeTimer = pending ? window.setTimeout(() => {
        badgeTimer = null;
        updateSoldOutBadges();
      }, Math.min(Math.max(0, BADGE_DEADLINE - Date.now()), 2147483647)) : null;
    };

    const element = (className, parent) => {
      const node = document.createElement("div");
      node.className = className;
      if (parent) parent.appendChild(node);
      return node;
    };
    const decorative = (node) => {
      node.setAttribute("aria-hidden", "true");
      node.setAttribute("inert", "");
      node.style.pointerEvents = "none";
      return node;
    };
    const addClaw = (parent) => {
      const claw = element("lj-arcade-claw", parent);
      element("lj-arcade-claw-cap", claw);
      element("lj-arcade-finger-left", claw);
      element("lj-arcade-finger-right", claw);
      return claw;
    };
    const mark = (node, className) => {
      if (!node || node.classList.contains(className)) return;
      node.classList.add(className);
      if (!marked.has(node)) marked.set(node, new Set());
      marked.get(node).add(className);
    };
    const removeHeaderLanterns = () => {
      if (headerLanterns) headerLanterns.remove();
      headerLanterns = null;
      lanternHeader = null;
    };
    const mountHeaderLanterns = () => {
      const header = [...document.querySelectorAll(".section-header")].find((node) => !unloadedHeaders.has(node));
      if (!header?.parentElement) { removeHeaderLanterns(); return; }
      if (!headerLanterns) {
        headerLanterns = decorative(element("lj-header-lanterns"));
        for (let strandIndex = 0; strandIndex < 2; strandIndex += 1) {
          const strand = element("lj-header-lantern-strand", headerLanterns);
          element("lj-header-lantern-cord", strand);
          for (let index = 0; index < 5; index += 1) {
            const lantern = element("lj-header-lantern", strand);
            lantern.style.setProperty("--lantern-index", String(index));
            lantern.style.setProperty("--lantern-strand", String(strandIndex));
            element("lj-header-lantern-paper", lantern);
            element("lj-header-lantern-tassel", lantern);
          }
        }
      }
      // The Shopify section itself becomes sticky. Keep this decorative strip
      // as its preceding sibling, after the announcement and outside the header.
      if (headerLanterns.parentElement !== header.parentElement || headerLanterns.nextElementSibling !== header) {
        header.parentElement.insertBefore(headerLanterns, header);
      }
      lanternHeader = header;
    };
    const headerSectionsWithin = (root) => {
      if (!(root instanceof Element)) return [];
      return [...(root.matches(".section-header") ? [root] : []), ...root.querySelectorAll(".section-header")];
    };
    const cleanupDecorations = () => {
      removeHeaderLanterns();
      for (const node of decorations) node.remove();
      decorations.clear();
      for (const [node, classes] of marked) for (const name of classes) node.classList.remove(name);
      marked.clear();
    };

    const clearTimers = (transfer) => {
      for (const timer of transfer.timers) window.clearTimeout(timer);
    };
    const cancelAnimations = (transfer) => {
      for (const animation of transfer.animations) {
        try { animation.cancel(); } catch (_) { /* Cleanup must not block navigation. */ }
      }
    };
    const restoreStyle = (node, style) => {
      if (style === null) node.removeAttribute("style");
      else node.setAttribute("style", style);
    };
    const cleanup = () => {
      const transfer = active;
      if (!transfer) return;
      active = null;
      clearTimers(transfer);
      cancelAnimations(transfer);
      transfer.card.classList.remove("lj-arcade-picked");
      for (const { node, style } of transfer.childStyles) restoreStyle(node, style);
      if (transfer.borrowed) {
        const { node, parent, next, style } = transfer.borrowed;
        node.classList.remove("lj-arcade-transfer-trolley");
        restoreStyle(node, style);
        parent.insertBefore(node, next?.parentElement === parent ? next : null);
      }
      transfer.overlay.remove();
    };
    const finish = (navigate) => {
      if (!navigate) { cleanup(); return; }
      const transfer = active;
      if (!transfer || transfer.navigating) return;
      transfer.navigating = true;
      clearTimers(transfer);
      try {
        // Commit the final pose before retiring animation effects. Leave the
        // capsule suspended while the destination document is loading.
        transfer.freeze();
        cancelAnimations(transfer);
        window.location.assign(transfer.destination);
      } catch (_) {
        // A blocked navigation must leave the original card usable.
        cleanup();
      }
    };

    const removeCartBurst = (burst) => {
      if (!cartBursts.delete(burst)) return;
      window.clearTimeout(burst.timer);
      cancelAnimations(burst);
      burst.node.remove();
    };
    const clearCartBursts = () => {
      for (const burst of cartBursts) removeCartBurst(burst);
    };
    const decorateCartQuantity = (event, target) => {
      if (!motionAllowed() || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const button = target.closest("button[name='plus'], button[name='minus']");
      if (!button || !button.closest("cart-items") || button.closest("cart-drawer, cart-drawer-items, #CartDrawer, .cart-drawer")
        || button.disabled || button.hasAttribute("disabled") || button.classList.contains("disabled") || button.getAttribute("aria-disabled") === "true"
        || typeof button.animate !== "function") return;
      let burst;
      try {
        const bounds = button.getBoundingClientRect();
        if (![bounds.left, bounds.top, bounds.width, bounds.height].every(Number.isFinite) || bounds.width <= 0 || bounds.height <= 0) return;
        while (cartBursts.size >= CART_BURST_LIMIT) removeCartBurst(cartBursts.values().next().value);
        const node = decorative(element("lj-arcade-cart-burst", document.body));
        Object.assign(node.style, {
          position: "fixed", width: "0", height: "0",
          left: `${Math.max(0, Math.min(window.innerWidth, bounds.left + bounds.width / 2))}px`,
          top: `${Math.max(0, Math.min(window.innerHeight, bounds.top + bounds.height / 2))}px`,
        });
        burst = { node, animations: [], timer: null };
        cartBursts.add(burst);
        burst.timer = window.setTimeout(() => removeCartBurst(burst), 800);
        const colors = [["#d7c5ed", "#a894bf"], ["#bce1d9", "#83b7af"], ["#f2bbd0", "#cd8baa"]];
        for (let index = 0; index < CART_CAPSULE_COUNT; index += 1) {
          const capsule = element("lj-arcade-cart-capsule", node);
          const angle = index / CART_CAPSULE_COUNT * Math.PI * 2;
          const x = Math.cos(angle) * (48 + index % 3 * 6);
          const y = Math.sin(angle) * 38;
          const turn = index % 2 ? 115 : -115;
          const color = colors[index % colors.length];
          capsule.style.setProperty("--capsule-color", color[0]);
          capsule.style.setProperty("--capsule-edge", color[1]);
          const animation = capsule.animate([
            { transform: "translate(-50%, -50%) scale(.55)", opacity: 0.95, offset: 0 },
            { transform: `translate(calc(-50% + ${x * 0.65}px), calc(-50% + ${y * 0.65 - 28}px)) rotate(${turn * 0.5}deg) scale(1)`, opacity: 0.88, offset: 0.5 },
            { transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y + 8}px)) rotate(${turn}deg) scale(.35)`, opacity: 0, offset: 1 },
          ], { duration: 560 + index % 3 * 40, easing: "ease-out", fill: "forwards" });
          animation.finished.catch(() => {});
          burst.animations.push(animation);
        }
        Promise.all(burst.animations.map((animation) => animation.finished)).then(
          () => removeCartBurst(burst), () => removeCartBurst(burst),
        );
      } catch (_) {
        if (burst) removeCartBurst(burst);
      }
      // Native quantity handlers may already have prevented the button's form
      // submission. We never intercept, submit or change their quantity value.
    };

    const refresh = () => {
      updateSoldOutBadges();
      if (!motionAllowed()) clearCartBursts();
      if (!isArcade()) {
        finish(false);
        cleanupDecorations();
        return;
      }
      mountHeaderLanterns();
      document.querySelectorAll(".collection-hero").forEach((hero) => {
        mark(hero, "lj-arcade-marquee");
        mark(hero.closest(".shopify-section"), "lj-arcade-hero-section");
      });
      document.querySelectorAll("#ProductGridContainer").forEach((grid) => {
        const section = grid.closest(".shopify-section") || grid.closest("section");
        if (!section) return;
        mark(section, "lj-arcade-machine");
        if (!section.querySelector(".lj-arcade-rig")) {
          const rig = decorative(element("lj-arcade-rig"));
          element("lj-arcade-rail", rig);
          const trolley = element("lj-arcade-trolley", rig);
          element("lj-arcade-cable", trolley);
          addClaw(trolley);
          section.insertBefore(rig, section.firstChild);
          decorations.add(rig);
        }
        if (!section.querySelector(".lj-arcade-plinth")) {
          const plinth = decorative(element("lj-arcade-plinth", section));
          element("lj-arcade-slot", plinth);
          element("lj-arcade-joystick", plinth);
          element("lj-arcade-arcade-button", plinth);
          decorations.add(plinth);
        }
      });
      for (const node of decorations) if (!node.isConnected) decorations.delete(node);
      for (const node of marked.keys()) if (!node.isConnected) marked.delete(node);
      if (active && (!active.card.isConnected || !motionAllowed())) finish(active.card.isConnected);
    };

    const destinationFor = (event, target) => {
      if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return null;
      if (target.closest("button, form, input, select, textarea, summary, [role='button'], .quick-add")) return null;
      const link = target.closest("a[href]");
      const card = target.closest(".product-card-wrapper");
      if (!link || !card || !card.contains(link) || link.hasAttribute("download")) return null;
      const baseTarget = document.querySelector("base[target]")?.getAttribute("target");
      if ((link.getAttribute("target") || baseTarget || "_self").toLowerCase() !== "_self") return null;
      const raw = link.getAttribute("href");
      if (!raw || raw.startsWith("#")) return null;
      try {
        const destination = new URL(raw, window.location.href);
        if (destination.origin !== window.location.origin || !/^https?:$/.test(destination.protocol)
          || !/\/products\/[^/]+/.test(destination.pathname)) return null;
        return { card, link, destination: destination.href };
      } catch (_) { return null; }
    };

    const beginTransfer = (event, selection) => {
      const { card, link, destination } = selection;
      const machine = card.closest(".lj-arcade-machine");
      const photo = card.querySelector(".card__media img") || card.querySelector("img");
      const rig = machine?.querySelector(".lj-arcade-rig");
      const parkedTrolley = rig?.querySelector(".lj-arcade-trolley");
      const rail = rig?.querySelector(".lj-arcade-rail");
      if (!photo || typeof card.animate !== "function" || !photo.complete || photo.naturalWidth <= 0
        || window.getComputedStyle(machine || card).getPropertyValue("--lj-arcade-ready").trim() !== "1") return;
      const source = photo.currentSrc || photo.getAttribute("src");
      const envelope = card.querySelector(".card__inner") || photo;
      const bounds = envelope.getBoundingClientRect();
      if (!source || bounds.width < 10 || bounds.height < 10 || bounds.bottom <= 0 || bounds.top >= window.innerHeight) return;
      // Keep the physical rail as the origin even when scrolling puts it out
      // of view. The fixed overlay clips the cable at the viewport boundary.
      const railBounds = rail?.getBoundingClientRect();
      if (machine && (!parkedTrolley || !railBounds
        || ![railBounds.left, railBounds.top, railBounds.width, railBounds.height].every(Number.isFinite)
        || railBounds.width <= 0 || railBounds.height <= 0)) return;

      let overlay;
      try {
        overlay = decorative(element("lj-arcade-transfer"));
        Object.assign(overlay.style, { position: "fixed", inset: "0", zIndex: "2147483000", overflow: "hidden" });
        const centerX = bounds.left + bounds.width / 2;
        const centerY = bounds.top + bounds.height / 2;
        let trolley, trolleyBounds, clawBounds, cableHeight, anchorY, borrowed = null;
        if (parkedTrolley) {
          trolley = parkedTrolley;
          const claw = trolley.querySelector(".lj-arcade-claw");
          const cable = trolley.querySelector(".lj-arcade-cable");
          if (!claw || !cable) { overlay.remove(); return; }
          trolleyBounds = trolley.getBoundingClientRect();
          clawBounds = claw.getBoundingClientRect();
          cableHeight = cable.getBoundingClientRect().height;
          anchorY = railBounds.bottom;
          borrowed = { node: trolley, parent: trolley.parentElement, next: trolley.nextElementSibling, style: trolley.getAttribute("style") };
        } else {
          // Featured cards have no cabinet. Give the temporary claw a short
          // local rail above the card, rather than a cable from the viewport.
          anchorY = Math.max(25, bounds.top - 145);
          const localRail = element("lj-arcade-rail", overlay);
          const railLeft = Math.max(12, bounds.left - 35);
          const railRight = Math.min(window.innerWidth - 12, bounds.right + 35);
          Object.assign(localRail.style, { top: `${anchorY - 13}px`, left: `${railLeft}px`, right: "auto", width: `${Math.max(68, railRight - railLeft)}px` });
          trolley = element("lj-arcade-trolley");
          element("lj-arcade-cable", trolley);
          addClaw(trolley);
          trolleyBounds = { left: centerX - 34, top: anchorY - 19, width: 68, height: 25 };
          clawBounds = { top: trolleyBounds.top + 51 };
          cableHeight = 34;
        }
        const capsule = element("lj-arcade-transfer-capsule", overlay);
        const cardStyle = window.getComputedStyle(card);
        for (const property of ["--capsule-color", "--capsule-edge"]) {
          const value = cardStyle.getPropertyValue(property).trim();
          if (value) capsule.style.setProperty(property, value);
        }
        const image = document.createElement("img");
        image.src = source;
        image.alt = "";
        image.draggable = false;
        capsule.appendChild(image);

        const startX = trolleyBounds.left + trolleyBounds.width / 2;
        const travelX = centerX - startX;
        const descent = Math.max(0, bounds.top + Math.min(bounds.height * 0.33, 80) - 44 - clawBounds.top);
        // Stop the prize below the rail. Neither prize nor claw can be hauled
        // upward into the sign, navigation bar or top of the viewport.
        const lift = Math.max(0, Math.min(descent, bounds.top - anchorY - 14));
        Object.assign(capsule.style, { position: "absolute", width: `${bounds.width}px`, height: `${bounds.height}px`, left: `${centerX - bounds.width / 2}px`, top: `${centerY - bounds.height / 2}px` });
        document.body.appendChild(overlay);
        const claw = trolley.querySelector(".lj-arcade-claw");
        const cable = trolley.querySelector(".lj-arcade-cable");
        const transfer = {
          card, link, destination, overlay, borrowed, animations: [], timers: [], navigating: false,
          childStyles: [claw, cable].map((node) => ({ node, style: node.getAttribute("style") })),
          freeze() {
            trolley.style.transform = `translateX(${travelX}px)`;
            claw.style.transform = `translateY(${descent - lift}px)`;
            cable.style.height = `${cableHeight + descent - lift}px`;
            capsule.style.transform = `translateY(${-lift}px)`;
            capsule.style.opacity = "1";
            overlay.classList.add("is-grabbing");
            card.classList.add("lj-arcade-picked");
          },
        };
        active = transfer;
        event.preventDefault();
        // Move the existing decorative assembly itself. The cabinet never
        // retains a second parked claw while its pickup is in progress.
        overlay.appendChild(trolley);
        trolley.classList.add("lj-arcade-transfer-trolley");
        Object.assign(trolley.style, { position: "absolute", left: `${trolleyBounds.left}px`, top: `${trolleyBounds.top}px`, width: `${trolleyBounds.width}px`, height: `${trolleyBounds.height}px`, margin: "0" });
        // All essential visual pieces now exist; navigation has a strict bound.
        transfer.timers.push(window.setTimeout(() => { if (active === transfer) finish(true); }, FAILSAFE));
        const settings = { duration: DURATION, fill: "forwards", easing: "ease-in-out" };
        const animate = (node, frames) => {
          const animation = node.animate(frames, settings);
          // Also handle cancellation if a later animation fails to initialize.
          animation.finished.catch(() => {});
          transfer.animations.push(animation);
        };
        animate(trolley, [
          { transform: "translateX(0)", offset: 0 },
          { transform: `translateX(${travelX}px)`, offset: 0.25 },
          { transform: `translateX(${travelX}px)`, offset: 1 },
        ]);
        animate(trolley.querySelector(".lj-arcade-claw"), [
          { transform: "translateY(0)", offset: 0 },
          { transform: "translateY(0)", offset: 0.25 },
          { transform: `translateY(${descent}px)`, offset: 0.54 },
          { transform: `translateY(${descent}px)`, offset: 0.67 },
          { transform: `translateY(${descent - lift}px)`, offset: 1 },
        ]);
        animate(trolley.querySelector(".lj-arcade-cable"), [
          { height: `${cableHeight}px`, offset: 0 },
          { height: `${cableHeight}px`, offset: 0.25 },
          { height: `${cableHeight + descent}px`, offset: 0.54 },
          { height: `${cableHeight + descent}px`, offset: 0.67 },
          { height: `${cableHeight + descent - lift}px`, offset: 1 },
        ]);
        animate(capsule, [
          { transform: "translateY(0)", opacity: 0, offset: 0 },
          { transform: "translateY(0)", opacity: 0, offset: 0.66 },
          { transform: "translateY(0)", opacity: 1, offset: 0.67 },
          { transform: `translateY(${-lift}px)`, opacity: 1, offset: 1 },
        ]);
        transfer.timers.push(window.setTimeout(() => { if (active === transfer) overlay.classList.add("is-grabbing"); }, DURATION * 0.54));
        transfer.timers.push(window.setTimeout(() => { if (active === transfer) card.classList.add("lj-arcade-picked"); }, DURATION * 0.67));
        Promise.all(transfer.animations.map((animation) => animation.finished)).then(
          () => { if (active === transfer) finish(true); },
          () => { if (active === transfer) finish(true); },
        );
      } catch (_) {
        if (active) finish(true);
        else if (overlay) overlay.remove();
      }
    };

    document.addEventListener("click", (event) => {
      const target = event.target instanceof Element ? event.target : event.target?.parentElement;
      if (!target) return;
      decorateCartQuantity(event, target);
      const selection = destinationFor(event, target);
      if (active) {
        if (selection?.destination === active.destination) { event.preventDefault(); return; }
        // A later navigation or another product takes precedence over this one.
        if (target.closest("a[href], button, form, input, select, textarea, summary, [role='button'], .quick-add")) {
          const alreadyNavigating = active.navigating;
          finish(false);
          if (alreadyNavigating) return;
        }
      }
      if (selection && motionAllowed()) beginTransfer(event, selection);
    });
    document.addEventListener("keydown", (event) => { if (event.key === "Escape") { finish(false); clearCartBursts(); } });
    document.addEventListener("submit", () => finish(false));
    document.addEventListener("visibilitychange", () => {
      updateSoldOutBadges();
      if (document.hidden) { clearCartBursts(); finish(true); }
    });
    window.addEventListener("scroll", () => { clearCartBursts(); finish(true); }, { passive: true });
    window.addEventListener("resize", () => { clearCartBursts(); finish(true); }, { passive: true });
    window.addEventListener("pagehide", () => { suspended = true; clearCartBursts(); if (!active?.navigating) finish(false); });
    window.addEventListener("pageshow", () => { suspended = false; clearCartBursts(); finish(false); refresh(); });
    window.addEventListener("popstate", () => finish(false));
    window.addEventListener("hashchange", () => finish(false));
    document.addEventListener("lymia:appearance", refresh);
    document.addEventListener("shopify:section:load", (event) => {
      for (const header of headerSectionsWithin(event?.target)) unloadedHeaders.delete(header);
      refresh();
    });
    document.addEventListener("shopify:section:unload", (event) => {
      clearCartBursts();
      for (const header of headerSectionsWithin(event.target)) unloadedHeaders.add(header);
      if (lanternHeader && event.target?.contains(lanternHeader)) removeHeaderLanterns();
      if (active && event.target?.contains(active.card)) finish(false);
    });
    const motionChanged = () => { if (!motionAllowed()) { clearCartBursts(); finish(true); } };
    if (reduced.addEventListener) reduced.addEventListener("change", motionChanged);
    else reduced.addListener(motionChanged);

    if (typeof MutationObserver === "function") {
      const observer = new MutationObserver((records) => {
        if (records.some((record) => record.type === "attributes")) { refresh(); motionChanged(); }
        const badgeChanged = records.some((record) => {
          if (record.type !== "characterData" && record.type !== "childList") return false;
          const target = record.target instanceof Element ? record.target : record.target?.parentElement;
          if (target?.closest(".badge, .price__badge-sold-out")) return true;
          return [...(record.addedNodes || []), ...(record.removedNodes || [])].some((node) => node instanceof Element
            && (node.matches(".badge, .card__badge, .price__badge-sold-out") || node.querySelector(BADGE_SELECTOR)));
        });
        if (badgeChanged) updateSoldOutBadges();
        const relevant = records.some((record) => record.type === "childList"
          && [...record.addedNodes, ...record.removedNodes].some((node) => node instanceof Element
            && (node.matches(".shopify-section, .section-header, .collection-hero, #ProductGridContainer, .product-card-wrapper")
              || node.querySelector(".section-header, .collection-hero, #ProductGridContainer, .product-card-wrapper"))));
        if (relevant && refreshTimer === null) refreshTimer = window.setTimeout(() => { refreshTimer = null; refresh(); }, 0);
      });
      observer.observe(document.body, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ["data-lj-motion-state"] });
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-lymia-look"] });
    }
    app.refresh = refresh;
    refresh();
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot, { once: true });
  else boot();
})();
